import { ComboboxData } from "apps/events/types/ComboboxData";
import { ComboboxGroupData } from "apps/events/types/ComboboxData";
import { EnumOwnerType } from "gql/generated";
import { EnumTagEntityTypes, useTagManyLazyQuery } from "gql/generated";
import { EnumTagGroupType, useTagManyQuery } from "gql/generated";
import _ from "lodash";
import { useEffect, useMemo, useState } from "react";
import { useTagGroupContext } from "shared/contextProviders/tagGroups";
import { getMultilingualString } from "shared/utils";

type UseEventTagsProps = {
  isOrganization: boolean;
  organizationId: string;
};

export const useEventTags = ({
  isOrganization,
  organizationId,
}: UseEventTagsProps) => {
  /* Get all `GLOBAL` tag groups */
  const { getTagGroups } = useTagGroupContext();
  const tagGroupData = useMemo(
    () => getTagGroups({ type: [EnumTagGroupType.GlobalEvent] }),
    [getTagGroups],
  );

  // Load the global event tags (All users can access these tags)
  const [globalEventTags, setGlobalEventTags] = useState<ComboboxGroupData[]>(
    [],
  );
  const [globalEventTagsLoaded, setGlobalEventTagsLoaded] = useState(false);
  const { loading: isLoadingGlobalEventTags, error: globalEventTagsError } =
    useTagManyQuery({
      variables: {
        filter: {
          entityTypes: [EnumTagEntityTypes.Event],
          tagGroup: tagGroupData?.map(tagGroup => tagGroup._id),
        },
        limit: 10000,
      },
      onCompleted: data => {
        setGlobalEventTags(
          data?.tagMany
            ? _(data.tagMany)
                .groupBy("tagGroup.key")
                .map(function (tags, key) {
                  return {
                    group: key,
                    // Sort the tags within the group by label
                    tags: _.orderBy(
                      tags.map(tag => ({
                        value: tag._id,
                        label: getMultilingualString(tag.name),
                      })),
                      ["label"],
                      ["asc"],
                    ),
                  };
                })
                .sortBy("group")
                .value()
            : [],
        );
        setGlobalEventTagsLoaded(true);
      },
    });

  // Load organization specific event tags IF the user is an organizatino user
  const [organizationEventTags, setOrganizationEventTags] = useState<
    ComboboxData[]
  >([]);
  const [organizationEventTagsLoaded, setOrganizationEventTagsLoaded] =
    useState(false);
  const [
    loadOrganizationEventTags,
    {
      loading: isLoadingOrganizationEventTags,
      error: organizationEventTagsError,
    },
  ] = useTagManyLazyQuery();
  useEffect(() => {
    // Set this to true in case the user is not an organization user
    setOrganizationEventTagsLoaded(true);
    if (isOrganization && organizationId) {
      setOrganizationEventTagsLoaded(false);
      loadOrganizationEventTags({
        variables: {
          filter: {
            entityTypes: [EnumTagEntityTypes.Event],
            owner: {
              entityId: organizationId,
              type: EnumOwnerType.Organization,
            },
          },
          limit: 10000,
        },
        onCompleted: data => {
          setOrganizationEventTags(
            data?.tagMany
              ? _.sortBy(
                  data.tagMany.map(tag => ({
                    value: tag._id,
                    label: getMultilingualString(tag.name),
                  })),
                  "label",
                )
              : [],
          );
          setOrganizationEventTagsLoaded(true);
        },
      });
    }
  }, [isOrganization, organizationId, loadOrganizationEventTags]);

  return {
    globalEventTags,
    organizationEventTags,
    loading: isLoadingGlobalEventTags || isLoadingOrganizationEventTags,
    error: globalEventTagsError || organizationEventTagsError,
    globalEventTagsLoaded,
    organizationEventTagsLoaded,
  };
};
